import { useEffect, useState } from "react";
import {
  Box,
  Grid2 as Grid,
  Typography,
  Checkbox,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { TextField, SelectBox } from "../../common";
import {
  FaMoneyCheckAlt,
  FaCreditCard,
  FaMoneyBillWave,
  FaPlus,
} from "react-icons/fa";
import { RiBankFill } from "react-icons/ri";
import { IconType } from "react-icons";
import { FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { useAtom } from "jotai";
import { useFetchOmnitokensQuery } from "../../../api";
import { userAtom } from "../../../atoms";
import { CARD_LOGOS } from "../../../constants";
import { PaymentMethod, PaymentSource, Role, TokenType } from "../../../enums";
import {
  DropdownItem,
  InvoiceOverviewInfo,
  Omnitoken,
  PaymentDetails,
} from "../../../types";
import { formatCurrency, getCountryConfig } from "../../../utils";

const getPaymentMethods = (
  theme: Theme,
): (DropdownItem & {
  icon: IconType;
  iconBg: string;
  iconColor: string;
  sub: string;
})[] => [
  {
    key: "Card",
    value: PaymentMethod.Card,
    icon: FaCreditCard,
    iconBg: theme.palette.primary.main,
    iconColor: "#fff",
    sub: "Credit or debit card",
  },
  {
    key: "ACH",
    value: PaymentMethod.Ach,
    icon: RiBankFill,
    iconBg: theme.palette.payment.grayIconBg,
    iconColor: "#555",
    sub: "Bank account debit",
  },
  {
    key: "Check",
    value: PaymentMethod.Check,
    icon: FaMoneyCheckAlt,
    iconBg: theme.palette.payment.check,
    iconColor: "#6D4A7F",
    sub: "Manual check payment",
  },
  {
    key: "Cash",
    value: PaymentMethod.Cash,
    icon: FaMoneyBillWave,
    iconBg: theme.palette.accent.main,
    iconColor: "#fff",
    sub: "Manual cash payment",
  },
];

type Props = {
  invoice: InvoiceOverviewInfo | undefined;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<PaymentDetails>;
  orgId: number;
  accountId: number;
  paymentMethod: PaymentMethod;
  setPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod>>;
  paymentSource: PaymentSource | undefined;
  setPaymentSource: React.Dispatch<
    React.SetStateAction<PaymentSource | undefined>
  >;
  amountValue: number | undefined;
  onCancel: () => void;
};

export const DetailsForm = ({
  invoice,
  register,
  setValue,
  errors,
  orgId,
  accountId,
  paymentMethod,
  setPaymentMethod,
  paymentSource,
  setPaymentSource,
  amountValue,
  onCancel,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const paymentMethods = getPaymentMethods(theme);

  const { stateLabel, zipLabel, statesDropdown } = getCountryConfig(
    invoice?.address?.country,
  );

  const [user] = useAtom(userAtom);

  const [selectedOmnitoken, setSelectedOmnitoken] = useState<
    Omnitoken | undefined
  >(undefined);

  const [savedOmnitokens, setSavedOmnitokens] = useState<Omnitoken[]>([]);

  const fetchOmnitokensQuery = useFetchOmnitokensQuery(orgId, accountId, {
    enabled: !!orgId && !!accountId,
  });

  useEffect(() => {
    setValue("paymentMethod", paymentMethod);
  }, [paymentMethod, setValue]);

  useEffect(() => {
    if (!selectedOmnitoken) {
      setValue("omnitokenId", "");
      setValue("tokenId", "");
      setValue("expirationMonth", "");
      setValue("expirationYear", "");
      setValue("netTransactionId", "");
      setValue("cardLogo", "");
      setValue("tokenType", "");

      return;
    }

    setValue("omnitokenId", selectedOmnitoken.id);
    setValue("tokenId", selectedOmnitoken.tokenId || "");
    setValue("expirationMonth", selectedOmnitoken.expirationMonth || "");
    setValue("expirationYear", selectedOmnitoken.expirationYear || "");
    setValue("netTransactionId", selectedOmnitoken.netTransactionId || "");
    setValue("cardLogo", selectedOmnitoken.cardLogo || "");
    setValue("tokenType", selectedOmnitoken.tokenType || "");
  }, [selectedOmnitoken, setValue]);

  useEffect(() => {
    if (fetchOmnitokensQuery.isSuccess) {
      setSavedOmnitokens(fetchOmnitokensQuery.data.omnitokens);
    }
  }, [fetchOmnitokensQuery.isSuccess, fetchOmnitokensQuery.data]);

  const visiblePaymentMethods = paymentMethods.filter((item) => {
    if (!user) {
      return item.value === PaymentMethod.Card;
    }

    if (user.roleCode === Role.SuperAdmin || user.roleCode === Role.Admin) {
      return true;
    }

    return (
      item.value !== PaymentMethod.Cash && item.value !== PaymentMethod.Check
    );
  });

  const savedOmnitokensForActivePaymentMethod = savedOmnitokens.filter(
    (omnitoken) => {
      if (paymentMethod === PaymentMethod.Ach) {
        return omnitoken.tokenType === TokenType.Ach;
      }

      if (paymentMethod === PaymentMethod.Card) {
        return omnitoken.tokenType === TokenType.Card;
      }

      return false;
    },
  );

  const supportsSavedOmnitokens =
    paymentMethod === PaymentMethod.Card || paymentMethod === PaymentMethod.Ach;

  const handleSelectPaymentMethod = (method: PaymentMethod) => {
    setPaymentMethod(method);
    setPaymentSource(undefined);
    setSelectedOmnitoken(undefined);
  };

  const handleSelectSavedOmnitoken = (omnitoken: Omnitoken) => {
    setPaymentSource(PaymentSource.Saved);
    setSelectedOmnitoken(omnitoken);
  };

  const handleSelectNewMethod = () => {
    setPaymentSource(PaymentSource.New);
    setSelectedOmnitoken(undefined);
  };

  const showAddNewCardOrAchFields =
    paymentSource === PaymentSource.New &&
    (paymentMethod === PaymentMethod.Card ||
      paymentMethod === PaymentMethod.Ach);

  const showCheckFields = paymentMethod === PaymentMethod.Check;

  const isPaymentDetailsComplete = (() => {
    if (
      paymentMethod === PaymentMethod.Card ||
      paymentMethod === PaymentMethod.Ach
    ) {
      return paymentSource !== undefined;
    }

    if (paymentMethod === PaymentMethod.Check) {
      return true;
    }

    if (paymentMethod === PaymentMethod.Cash) {
      return true;
    }

    return false;
  })();

  const savedSectionLabel =
    paymentMethod === PaymentMethod.Ach ? "Saved Bank Accounts" : "Saved Cards";

  const addNewLabel =
    paymentMethod === PaymentMethod.Ach
      ? "Add new bank account"
      : "Add new card";

  const tabGridSize = isMobile ? 6 : 12 / visiblePaymentMethods.length;

  return (
    <Box
      sx={{
        flex: { xs: "none", md: 1 },
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        minHeight: { xs: "auto", md: 0 },
        height: { xs: "auto", md: "100%" },
        boxSizing: "border-box",
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        overflow: { xs: "visible", md: "hidden" },
        p: { xs: "16px 16px 0", sm: "20px 20px 0" },
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: theme.palette.payment.mutedFg,
          }}
        >
          Payment Method
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: 17, sm: 20 },
            fontWeight: 650,
            color: "#1a1a1a",
            mt: 0.5,
          }}
        >
          How would you like to pay?
        </Typography>
      </Box>

      <Box
        sx={{
          flex: { xs: "none", md: 1 },
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          minHeight: { xs: "auto", md: 0 },
          boxSizing: "border-box",
          overflowY: { xs: "visible", md: "auto" },
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2, sm: 3 },
          pr: { xs: 0, sm: 1 },
          mt: { xs: 2, sm: 3 },
          pb: { xs: 2, md: 0 },
        }}
      >
        <Grid
          container
          spacing={1}
          sx={{
            width: "100%",
            maxWidth: "100%",
            margin: 0,
            bgcolor: theme.palette.payment.muted,
            borderRadius: "12px",
            p: 0.75,
            boxSizing: "border-box",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {visiblePaymentMethods.map((item) => {
            const active = paymentMethod === item.value;

            return (
              <Grid
                key={item.value}
                size={tabGridSize}
                sx={{
                  display: "flex",
                  minWidth: 0,
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  flexShrink: 0,
                }}
              >
                <Box
                  onClick={() =>
                    handleSelectPaymentMethod(item.value as PaymentMethod)
                  }
                  sx={{
                    flex: 1,
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    minHeight: { xs: 64, sm: 84 },
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    borderRadius: "10px",
                    px: { xs: 1.25, sm: 2 },
                    py: { xs: 1, sm: 1.5 },
                    cursor: "pointer",
                    bgcolor: active
                      ? theme.palette.primary.main
                      : "transparent",
                    color: active ? "#fff" : theme.palette.payment.mutedFg,
                    boxShadow: active
                      ? `0 4px 12px ${theme.palette.primary.main}33`
                      : "none",
                    transition: "all .2s ease",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: 12.5, sm: 13 },
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {item.key}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: 10.5, sm: 11 },
                      opacity: 0.8,
                      mt: 0.4,
                      lineHeight: 1.2,
                      minWidth: 0,
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.sub}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {supportsSavedOmnitokens && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: theme.palette.payment.mutedFg,
              }}
            >
              {savedSectionLabel}
            </Typography>

            {fetchOmnitokensQuery.isLoading && (
              <Box
                sx={{
                  borderRadius: 2,
                  p: 3,
                  display: "flex",
                  justifyContent: "center",
                  border: `1px solid ${theme.palette.payment.grayBorder}`,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {savedOmnitokensForActivePaymentMethod.map((savedOmnitoken) => {
                const active = selectedOmnitoken?.id === savedOmnitoken.id;

                return (
                  <Box
                    key={savedOmnitoken.id}
                    onClick={() => handleSelectSavedOmnitoken(savedOmnitoken)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1.25, sm: 1.75 },
                      borderRadius: "10px",
                      p: {
                        xs: "10px 12px",
                        sm: "12px 14px",
                      },
                      cursor: "pointer",
                      border: `1px solid ${
                        active
                          ? theme.palette.primary.main
                          : theme.palette.payment.grayBorder
                      }`,
                      bgcolor: active
                        ? `${theme.palette.payment.blueLight}`
                        : "#fff",
                      boxShadow: active
                        ? `0 0 0 3px ${theme.palette.payment.blueLight}1A`
                        : "none",
                      transition: "all .15s",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 34, sm: 38 },
                        height: { xs: 34, sm: 38 },
                        borderRadius: "8px",
                        bgcolor: theme.palette.payment.muted,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {savedOmnitoken.tokenType === TokenType.Ach ? (
                        <RiBankFill size={18} color="#7B6C32" />
                      ) : CARD_LOGOS[savedOmnitoken.cardLogo] ? (
                        <img
                          src={CARD_LOGOS[savedOmnitoken.cardLogo]}
                          style={{
                            width: 26,
                            height: 26,
                            objectFit: "contain",
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      ) : (
                        <FaCreditCard
                          size={18}
                          color={theme.palette.payment.mutedFg}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: {
                            xs: 12.5,
                            sm: 13,
                          },
                          fontWeight: 500,
                          color: "#1a1a1a",
                          lineHeight: 1.3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {savedOmnitoken.cardLabel}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: {
                            xs: 10.5,
                            sm: 11,
                          },
                          color: theme.palette.payment.mutedFg,
                          mt: 0.25,
                        }}
                      >
                        {`•••• ${savedOmnitoken.cardLast4}`}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        flexShrink: 0,
                        border: `2px solid ${
                          active
                            ? theme.palette.primary.main
                            : theme.palette.payment.grayBorder
                        }`,
                        bgcolor: active
                          ? theme.palette.primary.main
                          : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {active && (
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: "#fff",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box
              onClick={handleSelectNewMethod}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.25, sm: 1.75 },
                borderRadius: "10px",
                p: {
                  xs: "10px 12px",
                  sm: "12px 14px",
                },
                cursor: "pointer",
                border: `1px dashed ${
                  paymentSource === PaymentSource.New
                    ? theme.palette.primary.main
                    : theme.palette.payment.grayBorder
                }`,
                bgcolor:
                  paymentSource === PaymentSource.New
                    ? `${theme.palette.payment.blueLight}0A`
                    : "transparent",
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  bgcolor: theme.palette.payment.blueLight,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FaPlus size={12} color={theme.palette.primary.main} />
              </Box>

              <Typography
                sx={{
                  fontSize: {
                    xs: 12.5,
                    sm: 13,
                  },
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                }}
              >
                {addNewLabel}
              </Typography>
            </Box>
          </Box>
        )}

        {showAddNewCardOrAchFields && (
          <Box
            sx={{
              border: `1px solid ${theme.palette.payment.grayBorder}`,
              borderRadius: "12px",
              p: { xs: 1.75, sm: 2.5 },
              bgcolor: theme.palette.payment.muted,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: theme.palette.payment.mutedFg,
              }}
            >
              Billing Address
            </Typography>

            <TextField
              label="Address Line 1"
              name="addressLine1"
              required
              register={register}
              error={errors.addressLine1}
              defaultValue={invoice?.address.addressLine1}
            />

            <TextField
              label="Address Line 2 (Optional)"
              name="addressLine2"
              register={register}
              error={errors.addressLine2}
              defaultValue={invoice?.address.addressLine2}
            />

            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="City"
                  name="city"
                  required
                  register={register}
                  error={errors.city}
                  defaultValue={invoice?.address.city}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <SelectBox
                  key={invoice?.address.state || "default"}
                  label={stateLabel}
                  name="state"
                  dropdown={statesDropdown}
                  required
                  register={register}
                  error={errors.state}
                  defaultValue={invoice?.address.state || ""}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label={zipLabel}
                  name="zip"
                  required
                  register={register}
                  error={errors.zip}
                  defaultValue={invoice?.address.zip}
                />
              </Grid>
            </Grid>

            <Box
              component="label"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderRadius: "10px",
                p: "10px 14px",
                cursor: "pointer",
                border: `1px solid ${theme.palette.payment.grayBorder}`,
                bgcolor: "#fff",
              }}
            >
              <Checkbox {...register("saveCard")} sx={{ p: 0 }} />

              <Typography
                sx={{
                  fontSize: 13,
                  color: "#1a1a1a",
                }}
              >
                {paymentMethod === PaymentMethod.Ach
                  ? "Save bank account for future payments"
                  : "Save card for future payments"}
              </Typography>
            </Box>
          </Box>
        )}

        {showCheckFields && (
          <Box
            sx={{
              border: `1px solid ${theme.palette.payment.grayBorder}`,
              borderRadius: "12px",
              p: { xs: 1.75, sm: 2.5 },
              pb: { xs: 3, sm: 3.5 },
              bgcolor: theme.palette.payment.muted,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              mb: { xs: 2, sm: 0 },
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: theme.palette.payment.mutedFg,
              }}
            >
              Check Details
            </Typography>

            <TextField
              label="Bank Name"
              name="bankName"
              required
              register={register}
              error={errors.bankName}
            />

            <TextField
              label="Check Number"
              name="checkNumber"
              required
              register={register}
              error={errors.checkNumber}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          mt: { xs: 2, md: "auto" },
          flexShrink: 0,
          bgcolor: "#fff",
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          alignItems: {
            xs: "stretch",
            sm: "center",
          },
          justifyContent: "space-between",
          gap: {
            xs: 1.5,
            sm: 2,
          },
          borderTop: `1px solid ${theme.palette.payment.grayBorder}`,
          py: {
            xs: 1.5,
            sm: 2,
          },
        }}
      >
        <Box
          sx={{
            textAlign: {
              xs: "center",
              sm: "left",
            },
          }}
        >
          {!!invoice?.pendingAchAmount && invoice.pendingAchAmount > 0 && (
            <Typography
              sx={{
                fontSize: 11,
                color: "#B26A00",
                mb: 0.25,
              }}
            >
              {formatCurrency(invoice.pendingAchAmount)}
              already pending from a previous ACH payment
            </Typography>
          )}

          <Typography
            sx={{
              fontSize: 11,
              color: theme.palette.payment.mutedFg,
            }}
          >
            Total Amount
          </Typography>

          <Typography
            sx={{
              fontSize: {
                xs: 18,
                sm: 20,
              },
              fontWeight: 700,
              color: "#1a1a1a",
            }}
          >
            {formatCurrency(amountValue || 0)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={onCancel}
            sx={{
              padding: "12px 24px",
              borderRadius: "10px",
              fontSize: 14,
              fontWeight: 600,
              border: `1px solid ${theme.palette.payment.grayBorder}`,
              cursor: "pointer",
              bgcolor: "#fff",
              color: theme.palette.payment.mutedFg,
              whiteSpace: "nowrap",
              width: {
                xs: "100%",
                sm: "auto",
              },
              "&:hover": {
                bgcolor: theme.palette.payment.muted,
              },
            }}
          >
            Cancel
          </Box>

          <Box
            component="button"
            type="submit"
            form="paymentDetailsForm"
            disabled={!isPaymentDetailsComplete}
            sx={{
              padding: "12px 28px",
              borderRadius: "10px",
              fontSize: 14,
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
              bgcolor: "#2E7D32",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(46, 125, 50, 0.35)",
              whiteSpace: "nowrap",
              width: {
                xs: "100%",
                sm: "auto",
              },
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "#27692B",
                boxShadow: "0 6px 16px rgba(46, 125, 50, 0.45)",
              },
              "&:disabled": {
                bgcolor: "#A5D6A7",
                color: "#fff",
                cursor: "not-allowed",
              },
            }}
          >
            Continue to Payment
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
